import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import TimerIcon from '@mui/icons-material/Timer';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { modelLabTrainingJobApi } from '../../services/api';
import type { ModelLabTrainingJob, TrainingTelemetryResponse, TrainingTelemetrySeries } from '../../types';
import { ConfusionMatrix, FeatureImportance, LossChart, PRCurve, ROCCurve } from './charts';
import { formatMetricSummary, getApiErrorMessage, trainingStatusColor } from './utils';

type ProgressColor = 'primary' | 'success' | 'warning' | 'error' | 'info';

interface TrainingProgressState {
  value: number;
  determinate: boolean;
  label: string;
  detail: string;
  color: ProgressColor;
  currentEpoch?: number;
  totalEpochs?: number;
  lossHistory?: number[];
}

const isRunningStatus = (status: string) => status === 'RUNNING' || status === 'QUEUED';

const clampProgress = (value: number) => Math.max(0, Math.min(100, Number.isFinite(value) ? value : 0));

const getProgressPayload = (job: ModelLabTrainingJob): Record<string, any> => (
  job.progress && typeof job.progress === 'object' && !Array.isArray(job.progress)
    ? job.progress as Record<string, any>
    : {}
);

const parseDateMs = (value?: string | null) => {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const firstFiniteNumber = (...values: unknown[]) => {
  for (const value of values) {
    if (value == null || value === '') continue;
    const numeric = Number(value);
    if (Number.isFinite(numeric)) return numeric;
  }
  return null;
};

const formatDuration = (durationMs: number | null) => {
  if (durationMs == null || !Number.isFinite(durationMs) || durationMs < 0) return 'n/a';
  const totalSeconds = Math.max(0, Math.round(durationMs / 1000));
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
};

const formatDisplayNumber = (value: unknown, digits = 5) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 'n/a';
  if (Math.abs(numeric) >= 1000) return numeric.toLocaleString(undefined, { maximumFractionDigits: 2 });
  if (Math.abs(numeric) >= 10) return numeric.toFixed(3);
  return numeric.toFixed(digits);
};

const formatDisplayValue = (value: unknown) => {
  if (value == null || value === '') return 'n/a';
  if (typeof value === 'number') return formatDisplayNumber(value);
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return `${value.length} items`;
  if (typeof value === 'object') return `${Object.keys(value as Record<string, unknown>).length} fields`;
  return String(value);
};

const formatPercentValue = (value: unknown) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? `${numeric.toFixed(2)}%` : 'n/a';
};

const formatMemoryMb = (value: unknown) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 'n/a';
  if (numeric >= 1024) return `${(numeric / 1024).toFixed(2)} GB`;
  return `${numeric.toFixed(1)} MB`;
};

const formatResourceUsageValue = (key: string, value: unknown) => {
  if (key.includes('memory_rss_mb')) return formatMemoryMb(value);
  if (key.includes('memory_percent') || key.includes('cpu_percent')) return formatPercentValue(value);
  if (key.includes('time') && Number.isFinite(Number(value))) return formatDuration(Number(value) * 1000);
  return formatDisplayValue(value);
};

const humanizeKey = (value: string) => (
  value
    .replaceAll('.', ' ')
    .replaceAll('_', ' ')
    .replace(/\b\w/g, (match) => match.toUpperCase())
);

const useTrainingJobClock = (job: ModelLabTrainingJob) => {
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    if (!isRunningStatus(job.status)) {
      setNowMs(Date.now());
      return undefined;
    }

    setNowMs(Date.now());
    const intervalId = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(intervalId);
  }, [job.id, job.status]);

  return nowMs;
};

const getTrainingJobTiming = (job: ModelLabTrainingJob, state: TrainingProgressState, nowMs: number) => {
  const progress = getProgressPayload(job);
  const startedMs = parseDateMs(job.started_at);
  const completedMs = parseDateMs(job.completed_at);
  const explicitElapsedSeconds = firstFiniteNumber(progress.elapsed_seconds, progress.elapsed, progress.duration_seconds);
  const explicitRemainingSeconds = firstFiniteNumber(
    progress.eta_seconds,
    progress.estimated_remaining_seconds,
    progress.remaining_seconds,
    progress.time_remaining_seconds,
  );
  const terminal = job.status === 'COMPLETED' || job.status === 'FAILED' || job.status === 'CANCELLED';
  const elapsedMs = explicitElapsedSeconds != null
    ? explicitElapsedSeconds * 1000
    : startedMs != null
      ? Math.max(0, (completedMs ?? nowMs) - startedMs)
      : null;

  let etaLabel = 'n/a';
  if (job.status === 'COMPLETED') {
    etaLabel = 'done';
  } else if (job.status === 'FAILED' || job.status === 'CANCELLED') {
    etaLabel = 'stopped';
  } else if (job.status === 'QUEUED' && startedMs == null) {
    etaLabel = 'waiting';
  } else if (explicitRemainingSeconds != null && explicitRemainingSeconds >= 0) {
    etaLabel = formatDuration(explicitRemainingSeconds * 1000);
  } else if (state.determinate && state.value > 0 && state.value < 100 && elapsedMs != null) {
    etaLabel = formatDuration(elapsedMs * ((100 - state.value) / state.value));
  } else if (isRunningStatus(job.status) && state.determinate && state.value >= 99) {
    etaLabel = 'finishing';
  } else if (isRunningStatus(job.status)) {
    etaLabel = 'calculating';
  }

  const elapsedLabel = elapsedMs != null ? formatDuration(elapsedMs) : (job.status === 'QUEUED' ? 'not started' : 'n/a');
  const summaryLabel = job.status === 'QUEUED' && startedMs == null
    ? 'Waiting to start'
    : terminal
      ? `Elapsed ${elapsedLabel} - ${etaLabel}`
      : `Elapsed ${elapsedLabel} - ETA ${etaLabel}`;

  return { elapsedLabel, etaLabel, summaryLabel };
};

const useTrainingTelemetry = (job: ModelLabTrainingJob) => useQuery({
  queryKey: ['model-lab-training-telemetry', job.id],
  queryFn: () => modelLabTrainingJobApi.telemetry(job.id),
  enabled: Boolean(job.id),
  refetchInterval: false,
  refetchIntervalInBackground: false,
  staleTime: 5000,
});

const getSeriesValueCount = (series?: TrainingTelemetrySeries | null) => (
  (series?.points || []).filter((point) => Number.isFinite(Number(point.value))).length
);

const findTelemetrySeries = (
  telemetry: TrainingTelemetryResponse | undefined,
  metric: string,
) => telemetry?.series.find((series) => series.metric === metric);

const latestTelemetryValue = (
  telemetry: TrainingTelemetryResponse | undefined,
  metric: string,
) => findTelemetrySeries(telemetry, metric)?.latest ?? null;

const latestTelemetryValueFrom = (
  telemetry: TrainingTelemetryResponse | undefined,
  metrics: string[],
) => {
  for (const metric of metrics) {
    const value = latestTelemetryValue(telemetry, metric);
    if (value != null) return value;
  }
  return null;
};

const SummaryMetricCard: React.FC<{
  label: string;
  value: React.ReactNode;
  detail?: React.ReactNode;
}> = ({ label, value, detail }) => (
  <Grid item xs={12} sm={6} md={3}>
    <Paper variant="outlined" sx={{ p: 1.5, height: '100%', bgcolor: 'background.default' }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="h6" sx={{ lineHeight: 1.2, overflowWrap: 'anywhere' }}>
        {value}
      </Typography>
      {detail ? (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, overflowWrap: 'anywhere' }}>
          {detail}
        </Typography>
      ) : null}
    </Paper>
  </Grid>
);

const TelemetryLinePanel: React.FC<{
  series: TrainingTelemetrySeries;
  title?: string;
  color?: string;
}> = ({ series, title, color = '#f59e0b' }) => {
  const chartData = (series.points || [])
    .map((point, index) => {
      const value = Number(point.value);
      if (!Number.isFinite(value)) return null;
      const position = point.epoch ?? point.step ?? index + 1;
      return {
        index: index + 1,
        position,
        value,
        phase: point.phase || series.phase || 'training',
        split: point.split || series.split || '',
        timestamp: point.timestamp,
      };
    })
    .filter(Boolean) as Array<{
      index: number;
      position: number;
      value: number;
      phase: string;
      split: string;
      timestamp: string;
    }>;

  if (chartData.length < 2) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%', bgcolor: 'background.default' }}>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" noWrap>{title || humanizeKey(series.metric)}</Typography>
        <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" justifyContent="flex-end">
          <Chip size="small" variant="outlined" label={`latest ${formatDisplayNumber(series.latest)}`} />
          <Chip size="small" variant="outlined" label={`${chartData.length} points`} />
        </Stack>
      </Stack>
      <ResponsiveContainer width="100%" height={190}>
        <LineChart data={chartData} margin={{ top: 5, right: 12, bottom: 18, left: 4 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="position"
            tick={{ fontSize: 10 }}
            label={{ value: series.points.some((point) => point.epoch != null) ? 'Epoch' : 'Point', position: 'bottom', offset: 4 }}
          />
          <YAxis tick={{ fontSize: 10 }} width={54} domain={['auto', 'auto']} />
          <Tooltip
            formatter={(value) => [formatDisplayNumber(value), series.metric]}
            labelFormatter={(label) => `${series.points.some((point) => point.epoch != null) ? 'Epoch' : 'Point'} ${label}`}
          />
          <Line type="monotone" dataKey="value" stroke={color} dot={false} strokeWidth={2} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </Paper>
  );
};

const TrainingTelemetryCharts: React.FC<{
  telemetry: TrainingTelemetryResponse;
  excludeMetrics?: string[];
  includeMetrics?: string[];
}> = ({ telemetry, excludeMetrics = [], includeMetrics }) => {
  const excluded = new Set(excludeMetrics);
  const included = includeMetrics ? new Set(includeMetrics) : null;
  const allowed = (metric: string) => !excluded.has(metric) && (!included || included.has(metric));
  const preferred = [
    { metric: 'loss', title: 'Loss', color: '#ef4444' },
    { metric: 'best_loss', title: 'Best Loss', color: '#22c55e' },
    { metric: 'grad_norm', title: 'Gradient Norm', color: '#f59e0b' },
    { metric: 'lr', title: 'Learning Rate', color: '#38bdf8' },
    { metric: 'accuracy', title: 'Accuracy', color: '#22c55e' },
    { metric: 'f1_weighted', title: 'F1 Weighted', color: '#a78bfa' },
    { metric: 'resource.process_cpu_percent', title: 'Process CPU', color: '#fb923c' },
    { metric: 'process_cpu_percent', title: 'Process CPU', color: '#fb923c' },
    { metric: 'resource.system_cpu_percent', title: 'System CPU', color: '#f97316' },
    { metric: 'system_cpu_percent', title: 'System CPU', color: '#f97316' },
    { metric: 'resource.process_memory_rss_mb', title: 'Process RAM', color: '#60a5fa' },
    { metric: 'process_memory_rss_mb', title: 'Process RAM', color: '#60a5fa' },
    { metric: 'resource.system_memory_percent', title: 'System Memory', color: '#818cf8' },
    { metric: 'system_memory_percent', title: 'System Memory', color: '#818cf8' },
  ];
  const preferredMetrics = new Set(preferred.map((item) => item.metric));
  const preferredCharts = preferred
    .filter((item) => allowed(item.metric))
    .map((item) => ({ ...item, series: findTelemetrySeries(telemetry, item.metric) }))
    .filter((item) => getSeriesValueCount(item.series) > 1);
  const extraCharts = telemetry.series
    .filter((series) => allowed(series.metric))
    .filter((series) => !preferredMetrics.has(series.metric))
    .filter((series) => getSeriesValueCount(series) > 1)
    .slice(0, 6)
    .map((series) => ({ metric: series.metric, title: humanizeKey(series.metric), color: '#f97316', series }));
  const charts = [...preferredCharts, ...extraCharts];

  if (charts.length === 0) {
    return (
      <Alert severity="info">
        No chartable telemetry series is available yet. Running DL jobs usually populate loss, learning rate, and gradient series after the first epoch.
      </Alert>
    );
  }

  return (
    <Grid container spacing={2}>
      {charts.map(({ metric, title, color, series }) => (
        series ? (
          <Grid item xs={12} md={6} key={metric}>
            <TelemetryLinePanel series={series} title={title} color={color} />
          </Grid>
        ) : null
      ))}
    </Grid>
  );
};

export function getTrainingJobProgress(job: ModelLabTrainingJob): TrainingProgressState {
  const raw = job.progress;
  const progress = getProgressPayload(job);
  const numericProgress = typeof raw === 'number' ? raw : Number(progress.percent ?? progress.percentage ?? progress.progress);
  const currentEpoch = Number(progress.current_epoch ?? progress.epoch);
  const totalEpochs = Number(progress.total_epochs ?? progress.epochs);
  const hasEpochProgress = Number.isFinite(currentEpoch) && Number.isFinite(totalEpochs) && totalEpochs > 0;
  const phase = String(progress.phase || '').replaceAll('_', ' ');

  if (hasEpochProgress) {
    const value = clampProgress((currentEpoch / totalEpochs) * 100);
    return {
      value,
      determinate: true,
      label: `Epoch ${currentEpoch}/${totalEpochs}`,
      detail: progress.loss != null ? `loss ${Number(progress.loss).toFixed(6)}` : phase || `${Math.round(value)}%`,
      color: 'primary',
      currentEpoch,
      totalEpochs,
      lossHistory: Array.isArray(progress.loss_history) ? progress.loss_history : undefined,
    };
  }

  if (Number.isFinite(numericProgress)) {
    const value = clampProgress(numericProgress);
    return {
      value,
      determinate: true,
      label: `${Math.round(value)}%`,
      detail: phase || job.status.toLowerCase(),
      color: job.status === 'FAILED' ? 'error' : job.status === 'COMPLETED' ? 'success' : 'primary',
      lossHistory: Array.isArray(progress.loss_history) ? progress.loss_history : undefined,
    };
  }

  if (job.status === 'COMPLETED') {
    return { value: 100, determinate: true, label: 'Completed', detail: 'finished', color: 'success' };
  }
  if (job.status === 'FAILED') {
    return { value: 100, determinate: true, label: 'Failed', detail: job.error_message || 'failed', color: 'error' };
  }
  if (job.status === 'CANCELLED') {
    return { value: 100, determinate: true, label: 'Cancelled', detail: 'stopped', color: 'warning' };
  }
  if (job.status === 'QUEUED') {
    return { value: 0, determinate: false, label: 'Queued', detail: 'waiting for worker', color: 'info' };
  }
  if (job.status === 'RUNNING') {
    return { value: 0, determinate: false, label: phase || 'Running', detail: progress.algorithm ? `fitting ${progress.algorithm}` : 'training in progress', color: 'primary' };
  }
  return { value: 0, determinate: true, label: 'Draft', detail: 'not launched', color: 'info' };
}

export const TrainingJobProgressSummary: React.FC<{ job: ModelLabTrainingJob; compact?: boolean }> = ({ job, compact = false }) => {
  const state = getTrainingJobProgress(job);
  const nowMs = useTrainingJobClock(job);
  const timing = getTrainingJobTiming(job, state, nowMs);
  const showTiming = Boolean(job.started_at || job.completed_at || isRunningStatus(job.status));
  return (
    <Box sx={{ minWidth: compact ? 160 : 220 }}>
      <Stack direction="row" justifyContent="space-between" spacing={1}>
        <Typography variant="caption" color="text.secondary" noWrap>{state.label}</Typography>
        <Typography variant="caption" color="text.secondary" noWrap>{state.determinate ? `${Math.round(state.value)}%` : state.detail}</Typography>
      </Stack>
      <LinearProgress
        variant={state.determinate ? 'determinate' : 'indeterminate'}
        value={state.determinate ? state.value : undefined}
        color={state.color}
        sx={{ height: compact ? 6 : 8, borderRadius: 1, mt: 0.5 }}
      />
      {!compact && (
        <Typography variant="caption" color="text.secondary" noWrap display="block" sx={{ mt: 0.5 }}>
          {state.detail}
        </Typography>
      )}
      {showTiming && (
        <Typography variant="caption" color="text.secondary" noWrap display="block" sx={{ mt: 0.25 }}>
          {timing.summaryLabel}
        </Typography>
      )}
    </Box>
  );
};

export const TrainingJobMonitorCard: React.FC<{ job: ModelLabTrainingJob; onOpen: (job: ModelLabTrainingJob) => void }> = ({ job, onOpen }) => {
  const metricSummary = formatMetricSummary(job.metrics);
  return (
    <Paper
      variant="outlined"
      onClick={() => onOpen(job)}
      sx={{
        p: 1.25,
        bgcolor: 'background.paper',
        cursor: 'pointer',
        '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
      }}
    >
      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', lg: 'center' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
            <Typography variant="subtitle2">Job #{job.id}</Typography>
            <Chip size="small" label={job.status} color={trainingStatusColor(job.status)} />
            {job.generated_model_version_id ? <Chip size="small" variant="outlined" label={`Version #${job.generated_model_version_id}`} /> : null}
          </Stack>
          <Typography variant="caption" color="text.secondary" display="block" noWrap>
            Dataset: {job.dataset_version_name || `#${job.dataset_version}`}
            {job.feature_set_name ? ` - Features: ${job.feature_set_name}` : ''}
          </Typography>
          {job.error_message ? (
            <Typography variant="caption" color="error" display="block" noWrap sx={{ mt: 0.5 }}>
              {job.error_message}
            </Typography>
          ) : null}
        </Box>
        <TrainingJobProgressSummary job={job} />
        <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" justifyContent="flex-end" sx={{ minWidth: { lg: 220 } }}>
          {metricSummary.length > 0
            ? metricSummary.slice(0, 4).map((metric) => <Chip key={metric} size="small" variant="outlined" label={metric} />)
            : <Chip size="small" variant="outlined" label={isRunningStatus(job.status) ? 'Metrics pending' : 'No scalar metrics'} />}
        </Stack>
      </Stack>
    </Paper>
  );
};

const TrainingJobLogsPanel: React.FC<{ job: ModelLabTrainingJob }> = ({ job }) => {
  const progress = getTrainingJobProgress(job);
  return (
    <Stack spacing={2}>
      <Card variant="outlined" sx={{ bgcolor: 'action.hover' }}>
        <CardContent>
          <Typography variant="subtitle2" gutterBottom>Training Progress</Typography>
          <TrainingJobProgressSummary job={job} />
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
            {(job.progress as any)?.loss != null && <Chip size="small" label={`Loss: ${Number((job.progress as any).loss).toFixed(6)}`} variant="outlined" />}
            {(job.progress as any)?.best_loss != null && <Chip size="small" label={`Best: ${Number((job.progress as any).best_loss).toFixed(6)}`} color="success" variant="outlined" />}
            {(job.progress as any)?.lr != null && <Chip size="small" label={`LR: ${(job.progress as any).lr}`} variant="outlined" />}
          </Stack>
          {progress.lossHistory && progress.lossHistory.length > 1 ? (
            <Box sx={{ mt: 2 }}>
              <LossChart lossHistory={progress.lossHistory} title="Loss Curve (Live)" />
            </Box>
          ) : null}
        </CardContent>
      </Card>
      <TextField
        value={job.logs || job.error_message || 'No logs yet.'}
        multiline
        minRows={14}
        fullWidth
        InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: 13 } }}
      />
    </Stack>
  );
};

const TrainingJobMetricsPanel: React.FC<{ job: ModelLabTrainingJob }> = ({ job }) => {
  const telemetryQuery = useTrainingTelemetry(job);
  const scalarMetrics = Object.entries(job.metrics || {}).filter(([, value]) => typeof value === 'number');
  const hasFinalCharts = Boolean(
    job.metrics?.confusion_matrix ||
    job.metrics?.roc_curve ||
    job.metrics?.pr_curve ||
    (Array.isArray(job.metrics?.feature_importance) && job.metrics.feature_importance.length > 0),
  );

  return (
    <Stack spacing={3}>
      {telemetryQuery.isFetching && <LinearProgress />}
      {scalarMetrics.length === 0 && !hasFinalCharts ? (
        <Alert severity={isRunningStatus(job.status) ? 'info' : 'warning'}>
          Final evaluation metrics are not available yet. Live training telemetry is shown below when the worker records it.
        </Alert>
      ) : null}

      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle2" gutterBottom>Scalar Metrics</Typography>
          {scalarMetrics.length > 0 ? (
            <Grid container spacing={2}>
              {scalarMetrics.map(([key, value]) => (
                <SummaryMetricCard
                  key={key}
                  label={humanizeKey(key)}
                  value={Number(value).toFixed(4)}
                  detail={key}
                />
              ))}
            </Grid>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Accuracy, F1, recall, validation loss, and other final scalar metrics will appear after evaluation completes.
            </Typography>
          )}
        </CardContent>
      </Card>

      {telemetryQuery.error ? (
        <Alert severity="error">{getApiErrorMessage(telemetryQuery.error, 'Failed to load telemetry charts.')}</Alert>
      ) : telemetryQuery.data ? (
        <Card variant="outlined">
          <CardContent>
            <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
              <Typography variant="subtitle2">Live Training Charts</Typography>
              <Chip size="small" variant="outlined" label={`${telemetryQuery.data.summary.event_count} events`} />
              <Chip size="small" variant="outlined" label={`${telemetryQuery.data.summary.metric_count} metrics`} />
            </Stack>
            <TrainingTelemetryCharts telemetry={telemetryQuery.data} />
          </CardContent>
        </Card>
      ) : null}

      {(job.metrics || {}).cross_validation && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle2" gutterBottom>Cross-Validation</Typography>
            <Grid container spacing={2}>
              <SummaryMetricCard label="Mean" value={(job.metrics.cross_validation as any).mean?.toFixed(4) ?? 'n/a'} />
              <SummaryMetricCard label="Std" value={(job.metrics.cross_validation as any).std?.toFixed(4) ?? 'n/a'} />
              <SummaryMetricCard label="Folds" value={(job.metrics.cross_validation as any).scores?.length ?? 'n/a'} />
            </Grid>
          </CardContent>
        </Card>
      )}

      {hasFinalCharts ? <Divider /> : null}
      <Grid container spacing={3}>
        {job.metrics?.confusion_matrix && (
          <Grid item xs={12} md={6}>
            <Card variant="outlined"><CardContent><Typography variant="subtitle2" gutterBottom>Confusion Matrix</Typography><ConfusionMatrix data={job.metrics.confusion_matrix as any} /></CardContent></Card>
          </Grid>
        )}
        {job.metrics?.roc_curve && (
          <Grid item xs={12} md={6}>
            <Card variant="outlined"><CardContent><Typography variant="subtitle2" gutterBottom>ROC Curve</Typography><ROCCurve data={job.metrics.roc_curve as any} /></CardContent></Card>
          </Grid>
        )}
        {job.metrics?.pr_curve && (
          <Grid item xs={12} md={6}>
            <Card variant="outlined"><CardContent><Typography variant="subtitle2" gutterBottom>Precision-Recall Curve</Typography><PRCurve data={job.metrics.pr_curve as any} /></CardContent></Card>
          </Grid>
        )}
        {Array.isArray(job.metrics?.feature_importance) && job.metrics.feature_importance.length > 0 && (
          <Grid item xs={12} md={6}>
            <Card variant="outlined"><CardContent><Typography variant="subtitle2" gutterBottom>Feature Importance</Typography><FeatureImportance data={job.metrics.feature_importance as any} /></CardContent></Card>
          </Grid>
        )}
      </Grid>
    </Stack>
  );
};

const TrainingJobResourcesPanel: React.FC<{ job: ModelLabTrainingJob }> = ({ job }) => {
  const telemetryQuery = useTrainingTelemetry(job);
  const usage = job.metrics?.resource_usage as Record<string, any> | undefined;
  const state = getTrainingJobProgress(job);
  const nowMs = useTrainingJobClock(job);
  const timing = getTrainingJobTiming(job, state, nowMs);
  const progress = getProgressPayload(job);
  const config = job.config || {};
  const runtime = job.runtime_spec || {};
  const telemetry = telemetryQuery.data;
  const usageEntries = Object.entries(usage || {});
  const latestLoss = latestTelemetryValue(telemetry, 'loss') ?? progress.loss;
  const latestBestLoss = latestTelemetryValue(telemetry, 'best_loss') ?? progress.best_loss;
  const latestGradNorm = latestTelemetryValue(telemetry, 'grad_norm') ?? progress.grad_norm;
  const latestLr = latestTelemetryValue(telemetry, 'lr') ?? progress.lr;
  const latestProcessCpu = latestTelemetryValueFrom(telemetry, [
    'resource.process_cpu_percent',
    'process_cpu_percent',
    'resource_usage.process_cpu_percent',
  ]) ?? progress.process_cpu_percent ?? usage?.process_cpu_percent;
  const latestSystemCpu = latestTelemetryValueFrom(telemetry, [
    'resource.system_cpu_percent',
    'system_cpu_percent',
    'resource_usage.system_cpu_percent',
  ]) ?? progress.system_cpu_percent ?? usage?.system_cpu_percent;
  const latestProcessMemory = latestTelemetryValueFrom(telemetry, [
    'resource.process_memory_rss_mb',
    'process_memory_rss_mb',
    'resource_usage.process_memory_rss_mb',
  ]) ?? progress.process_memory_rss_mb ?? usage?.process_memory_rss_mb;
  const latestPeakMemory = latestTelemetryValueFrom(telemetry, [
    'resource.peak_process_memory_rss_mb',
    'peak_process_memory_rss_mb',
    'resource_usage.peak_process_memory_rss_mb',
  ]) ?? progress.peak_process_memory_rss_mb ?? usage?.peak_process_memory_rss_mb;
  const latestSystemMemory = latestTelemetryValueFrom(telemetry, [
    'resource.system_memory_percent',
    'system_memory_percent',
    'resource_usage.system_memory_percent',
  ]) ?? progress.system_memory_percent ?? usage?.system_memory_percent;
  const resourceMetricNames = [
    'resource.process_cpu_percent',
    'process_cpu_percent',
    'resource.system_cpu_percent',
    'system_cpu_percent',
    'resource.process_memory_rss_mb',
    'process_memory_rss_mb',
    'resource.peak_process_memory_rss_mb',
    'peak_process_memory_rss_mb',
    'resource.system_memory_percent',
    'system_memory_percent',
  ];
  const hasResourceCharts = Boolean(
    telemetry?.series.some((series) => resourceMetricNames.includes(series.metric) && getSeriesValueCount(series) > 1),
  );

  return (
    <Stack spacing={2}>
      {telemetryQuery.isFetching && <LinearProgress />}
      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <TimerIcon fontSize="small" color="action" />
            <Typography variant="subtitle2">Live Runtime State</Typography>
          </Stack>
          <Grid container spacing={2}>
            <SummaryMetricCard label="Status" value={job.status} detail={state.detail} />
            <SummaryMetricCard
              label="Progress"
              value={state.determinate ? `${Math.round(state.value)}%` : state.label}
              detail={state.currentEpoch && state.totalEpochs ? `epoch ${state.currentEpoch}/${state.totalEpochs}` : state.label}
            />
            <SummaryMetricCard label="Elapsed" value={timing.elapsedLabel} detail={`ETA ${timing.etaLabel}`} />
            <SummaryMetricCard label="Telemetry Events" value={telemetry?.summary.event_count ?? 'n/a'} detail={`${telemetry?.summary.metric_count ?? 0} metrics`} />
            <SummaryMetricCard label="Algorithm" value={progress.algorithm || config.algorithm || 'n/a'} detail={config.model_type || runtime.authoring_mode || ''} />
            <SummaryMetricCard label="Executor" value={runtime.executor || config.executor || 'n/a'} detail={runtime.compute_name || runtime.compute_spec_name || ''} />
            <SummaryMetricCard label="Dataset" value={job.dataset_version_name || `#${job.dataset_version}`} detail={`version id ${job.dataset_version}`} />
            <SummaryMetricCard label="Feature Set" value={job.feature_set_name || 'Raw dataset'} detail={job.feature_set ? `#${job.feature_set}` : 'no feature recipe'} />
            <SummaryMetricCard label="Target" value={config.target_column || 'n/a'} detail="training label" />
            <SummaryMetricCard label="Process CPU" value={formatPercentValue(latestProcessCpu)} detail={latestProcessCpu == null ? 'not recorded yet' : 'worker process'} />
            <SummaryMetricCard label="System CPU" value={formatPercentValue(latestSystemCpu)} detail={latestSystemCpu == null ? 'not recorded yet' : 'host/container view'} />
            <SummaryMetricCard label="Process RAM" value={formatMemoryMb(latestProcessMemory)} detail={latestProcessMemory == null ? 'not recorded yet' : 'RSS memory'} />
            <SummaryMetricCard label="Peak RAM" value={formatMemoryMb(latestPeakMemory)} detail={latestPeakMemory == null ? 'not recorded yet' : 'peak RSS sampled'} />
            <SummaryMetricCard label="System Memory" value={formatPercentValue(latestSystemMemory)} detail={latestSystemMemory == null ? 'not recorded yet' : 'host/container view'} />
            <SummaryMetricCard label="Latest Loss" value={formatDisplayNumber(latestLoss)} detail={latestLoss == null ? 'not recorded yet' : 'live telemetry'} />
            <SummaryMetricCard label="Best Loss" value={formatDisplayNumber(latestBestLoss)} detail={latestBestLoss == null ? 'not recorded yet' : 'live telemetry'} />
            <SummaryMetricCard label="Gradient Norm" value={formatDisplayNumber(latestGradNorm)} detail={latestGradNorm == null ? 'not recorded yet' : 'live telemetry'} />
            <SummaryMetricCard label="Learning Rate" value={formatDisplayValue(latestLr)} detail={latestLr == null ? 'not recorded yet' : 'live telemetry'} />
          </Grid>
        </CardContent>
      </Card>

      {telemetry && hasResourceCharts ? (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle2" gutterBottom>CPU and Memory Telemetry</Typography>
            <TrainingTelemetryCharts telemetry={telemetry} includeMetrics={resourceMetricNames} />
          </CardContent>
        </Card>
      ) : null}

      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle2" gutterBottom>Final Resource Usage</Typography>
          {usageEntries.length > 0 ? (
            <Grid container spacing={2}>
              {usageEntries.map(([key, value]) => (
                <SummaryMetricCard
                  key={key}
                  label={humanizeKey(key)}
                  value={formatResourceUsageValue(key, value)}
                  detail={key}
                />
              ))}
            </Grid>
          ) : (
            <Alert severity={isRunningStatus(job.status) ? 'info' : 'warning'}>
              Final resource usage has not been recorded yet. The live runtime state above is available while the job runs.
            </Alert>
          )}
        </CardContent>
      </Card>

      {telemetryQuery.error ? (
        <Alert severity="error">{getApiErrorMessage(telemetryQuery.error, 'Failed to load resource telemetry.')}</Alert>
      ) : null}
    </Stack>
  );
};

const TrainingJobTelemetryPanel: React.FC<{ job: ModelLabTrainingJob }> = ({ job }) => {
  const telemetryQuery = useTrainingTelemetry(job);

  if (telemetryQuery.isFetching && !telemetryQuery.data) return <LinearProgress />;
  if (telemetryQuery.error) return <Alert severity="error">{getApiErrorMessage(telemetryQuery.error, 'Failed to load telemetry.')}</Alert>;
  if (!telemetryQuery.data) {
    return (
      <Card variant="outlined"><CardContent><Typography variant="body2" color="text.secondary">No telemetry has been recorded for this job yet.</Typography></CardContent></Card>
    );
  }

  const telemetry = telemetryQuery.data;
  const lossSeries = telemetry.series.find((series) => series.metric === 'loss');
  const lossHistory = (lossSeries?.points || [])
    .map((point) => point.value)
    .filter((value): value is number => typeof value === 'number');
  const scalarSeries = telemetry.series
    .filter((series) => series.latest != null)
    .sort((a, b) => a.metric.localeCompare(b.metric));

  return (
    <Stack spacing={2}>
      {telemetryQuery.isFetching && <LinearProgress />}
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle2" gutterBottom>Telemetry Summary</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary">Events</Typography><Typography variant="h6">{telemetry.summary.event_count}</Typography></Grid>
            <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary">Metrics</Typography><Typography variant="h6">{telemetry.summary.metric_count}</Typography></Grid>
            <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary">Loss curve</Typography><Typography variant="h6">{telemetry.summary.has_loss_curve ? 'Yes' : 'No'}</Typography></Grid>
            <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary">Gradient stats</Typography><Typography variant="h6">{telemetry.summary.has_gradient_stats ? 'Yes' : 'No'}</Typography></Grid>
          </Grid>
        </CardContent>
      </Card>
      {lossHistory.length > 1 && <LossChart lossHistory={lossHistory} title="Training Loss Telemetry" />}
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle2" gutterBottom>Telemetry Charts</Typography>
          <TrainingTelemetryCharts telemetry={telemetry} excludeMetrics={lossHistory.length > 1 ? ['loss'] : []} />
        </CardContent>
      </Card>
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle2" gutterBottom>Scalar Series</Typography>
          <Grid container spacing={2}>
            {scalarSeries.slice(0, 24).map((series) => (
              <Grid item xs={6} md={3} key={`${series.metric}-${series.phase}-${series.split}`}>
                <Typography variant="caption" color="text.secondary" noWrap display="block">{series.metric}</Typography>
                <Typography variant="h6">{series.latest != null ? Number(series.latest).toFixed(5) : 'n/a'}</Typography>
                <Typography variant="caption" color="text.secondary">{series.points.length} point{series.points.length === 1 ? '' : 's'}</Typography>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    </Stack>
  );
};

export const TrainingJobDetailsDialog: React.FC<{
  job: ModelLabTrainingJob | null;
  open: boolean;
  onClose: () => void;
}> = ({ job, open, onClose }) => {
  const [tab, setTab] = useState(0);
  const selectedJobId = job?.id ?? null;
  const fullJobQuery = useQuery({
    queryKey: ['model-lab-training-job', selectedJobId],
    queryFn: () => modelLabTrainingJobApi.get(selectedJobId!),
    enabled: Boolean(open && selectedJobId),
    refetchInterval: false,
    refetchIntervalInBackground: false,
    staleTime: 5000,
  });
  const displayJob = fullJobQuery.data || job;
  const scalarMetrics = useMemo(() => displayJob ? formatMetricSummary(displayJob.metrics) : [], [displayJob]);

  return (
    <Dialog
      open={open}
      onClose={() => {
        setTab(0);
        onClose();
      }}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        Training Job Details
        {displayJob && (
          <Typography variant="body2" color="text.secondary">
            Job #{displayJob.id} - {displayJob.model_asset_name} - {displayJob.dataset_version_name}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        {displayJob && (
          <Box sx={{ mt: 1 }}>
            {fullJobQuery.isFetching && <LinearProgress sx={{ mb: 1 }} />}
            <Card variant="outlined" sx={{ mb: 2, bgcolor: 'action.hover' }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                      <Chip size="small" label={displayJob.status} color={trainingStatusColor(displayJob.status)} />
                      {(displayJob.config as any)?.algorithm && <Chip size="small" label={`Algorithm: ${(displayJob.config as any).algorithm}`} />}
                      {(displayJob.config as any)?.target_column && <Chip size="small" variant="outlined" label={`Target: ${(displayJob.config as any).target_column}`} />}
                      {displayJob.feature_set_name && <Chip size="small" variant="outlined" label={`Features: ${displayJob.feature_set_name}`} />}
                      {displayJob.generated_model_version_id && <Chip size="small" color="success" variant="outlined" label={`Version #${displayJob.generated_model_version_id}`} />}
                    </Stack>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                      Started: {displayJob.started_at ? new Date(displayJob.started_at).toLocaleString() : 'not started'} - Completed: {displayJob.completed_at ? new Date(displayJob.completed_at).toLocaleString() : 'pending'}
                    </Typography>
                  </Box>
                  <TrainingJobProgressSummary job={displayJob} />
                </Stack>
                {scalarMetrics.length > 0 && (
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                    {scalarMetrics.map((metric) => <Chip key={metric} size="small" variant="outlined" label={metric} />)}
                  </Stack>
                )}
              </CardContent>
            </Card>

            <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ mb: 2 }}>
              <Tab label="Logs" />
              <Tab label="Metrics & Charts" />
              <Tab label="Resource Usage" />
              <Tab label="Telemetry" />
            </Tabs>

            {tab === 0 && <TrainingJobLogsPanel job={displayJob} />}
            {tab === 1 && <TrainingJobMetricsPanel job={displayJob} />}
            {tab === 2 && <TrainingJobResourcesPanel job={displayJob} />}
            {tab === 3 && <TrainingJobTelemetryPanel job={displayJob} />}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => { setTab(0); onClose(); }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
